/**
 * Webtech HR Engine™ - Skill Campaign Detail Page
 * Détails d'une campagne d'évaluation des compétences
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Target,
  Play,
  Pause,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  User,
  ChevronRight,
  Eye,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: Play },
  closed: { label: 'Clôturée', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
};

const EVAL_STATUS = {
  pending: { label: 'En attente', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'En cours', color: 'bg-yellow-100 text-yellow-700' },
  self_completed: { label: 'Auto-éval. faite', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Terminé', color: 'bg-green-100 text-green-700' },
};

export default function SkillCampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.companyId;
  
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activateDialog, setActivateDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);

  useEffect(() => {
    if (companyId && campaignId) {
      loadCampaign();
    }
  }, [companyId, campaignId]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/skills/campaigns/${campaignId}?company_id=${companyId}`);
      setCampaign(response.data);
    } catch (error) {
      console.error('Error loading campaign:', error);
      toast.error('Erreur lors du chargement de la campagne');
      navigate('/skills/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      const res = await api.post(`/skills/campaigns/${campaignId}/activate?company_id=${companyId}`);
      toast.success(`Campagne activée avec ${res.data.participantsCreated} participants`);
      setActivateDialog(false);
      loadCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    }
  };

  const handleClose = async () => {
    try {
      await api.post(`/skills/campaigns/${campaignId}/close?company_id=${companyId}`);
      toast.success('Campagne clôturée');
      setCloseDialog(false);
      loadCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Détail campagne">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout title="Campagne">
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-amber-500" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">
            Campagne introuvable
          </h3>
          <p className="text-slate-600 mb-6 leading-relaxed">
            Cette campagne d'évaluation de compétences n'existe pas ou a été supprimée.
            Elle a peut-être été archivée ou vous n'avez pas les droits d'accès.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Button onClick={() => navigate('/skills/evaluations')} className="bg-purple-600 hover:bg-purple-700 text-white">
              Voir les campagnes
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const config = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const StatusIcon = config.icon;
  const participants = campaign.participants || [];
  
  // Calculate stats
  const completedCount = participants.filter(p => p.status === 'completed').length;
  const selfCompletedCount = participants.filter(p => p.status === 'self_completed').length;
  const pendingCount = participants.filter(p => p.status === 'pending' || p.status === 'in_progress').length;
  const progressPercent = participants.length > 0 
    ? Math.round((completedCount / participants.length) * 100) 
    : 0;

  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  return (
    <DashboardLayout title="Détail campagne">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/skills/campaigns')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux campagnes
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-webtech-blue/10 flex items-center justify-center">
                <Target className="h-6 w-6 text-webtech-blue" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900">{campaign.name}</h1>
                  <Badge className={config.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
                {campaign.description && (
                  <p className="text-slate-500 mt-1">{campaign.description}</p>
                )}
              </div>
            </div>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2">
              {campaign.status === 'draft' && (
                <Button onClick={() => setActivateDialog(true)}>
                  <Play className="h-4 w-4 mr-2" />
                  Activer
                </Button>
              )}
              {campaign.status === 'active' && (
                <Button variant="outline" onClick={() => setCloseDialog(true)}>
                  <Pause className="h-4 w-4 mr-2" />
                  Clôturer
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Calendar className="h-6 w-6 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">Période</p>
                <p className="text-sm font-medium">
                  {new Date(campaign.startDate).toLocaleDateString('fr-FR')} - {new Date(campaign.endDate).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-slate-900">{participants.length}</p>
                <p className="text-sm text-slate-500">Participants</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle2 className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold text-green-600">{completedCount}</p>
                <p className="text-sm text-slate-500">Terminés</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Clock className="h-6 w-6 mx-auto text-yellow-500 mb-2" />
                <p className="text-2xl font-bold text-yellow-600">{selfCompletedCount}</p>
                <p className="text-sm text-slate-500">Auto-éval. faites</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-slate-400 mb-2" />
                <p className="text-2xl font-bold text-slate-600">{pendingCount}</p>
                <p className="text-sm text-slate-500">En attente</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Progression globale</span>
              <span className="text-sm font-bold text-slate-900">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-3" />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="participants">Participants ({participants.length})</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ciblage</span>
                    <span className="font-medium">
                      {campaign.targetType === 'all' ? 'Tous les employés' : 
                       campaign.targetType === 'department' ? 'Par département' : 'Personnalisé'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Modes d'évaluation</span>
                    <div className="flex gap-2">
                      {campaign.evaluationModes?.includes('self') && (
                        <Badge variant="outline">Auto-évaluation</Badge>
                      )}
                      {campaign.evaluationModes?.includes('manager') && (
                        <Badge variant="outline">Manager</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Créée le</span>
                    <span className="font-medium">
                      {new Date(campaign.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  {campaign.activatedAt && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Activée le</span>
                      <span className="font-medium">
                        {new Date(campaign.activatedAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  )}
                  {campaign.closedAt && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Clôturée le</span>
                      <span className="font-medium">
                        {new Date(campaign.closedAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Répartition par statut</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                        <span className="text-sm">Terminés</span>
                      </div>
                      <span className="font-medium">{completedCount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                        <span className="text-sm">Auto-évaluation faite</span>
                      </div>
                      <span className="font-medium">{selfCompletedCount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                        <span className="text-sm">En cours</span>
                      </div>
                      <span className="font-medium">
                        {participants.filter(p => p.status === 'in_progress').length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-slate-300"></div>
                        <span className="text-sm">En attente</span>
                      </div>
                      <span className="font-medium">
                        {participants.filter(p => p.status === 'pending').length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Participants Tab */}
          <TabsContent value="participants" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collaborateur</TableHead>
                      <TableHead>Poste</TableHead>
                      <TableHead>Auto-évaluation</TableHead>
                      <TableHead>Éval. Manager</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {participants.map(participant => {
                      const emp = participant.employee;
                      const statusConfig = EVAL_STATUS[participant.status] || EVAL_STATUS.pending;
                      
                      return (
                        <TableRow key={participant.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-webtech-blue/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-webtech-blue">
                                  {emp?.firstName?.[0]}{emp?.lastName?.[0]}
                                </span>
                              </div>
                              <span className="font-medium">
                                {emp?.firstName} {emp?.lastName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {emp?.position || '-'}
                          </TableCell>
                          <TableCell>
                            {participant.selfEvaluation?.status === 'completed' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Fait
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500">
                                <Clock className="h-3 w-3 mr-1" />
                                En attente
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {participant.managerEvaluation?.status === 'completed' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Fait
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500">
                                <Clock className="h-3 w-3 mr-1" />
                                En attente
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/skills/employee/${emp?.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Voir profil
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {participants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          {campaign.status === 'draft' 
                            ? "Activez la campagne pour créer les participants"
                            : "Aucun participant"
                          }
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Activate Dialog */}
      <AlertDialog open={activateDialog} onOpenChange={setActivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activer la campagne</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action va créer les entrées de participation pour tous les employés ciblés.
              Les participants recevront une notification pour compléter leur évaluation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate}>
              <Play className="h-4 w-4 mr-2" />
              Activer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close Dialog */}
      <AlertDialog open={closeDialog} onOpenChange={setCloseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clôturer la campagne</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action va fermer la campagne. Les participants ne pourront plus soumettre d'évaluations.
              Êtes-vous sûr de vouloir continuer ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleClose} className="bg-yellow-600 hover:bg-yellow-700">
              <Pause className="h-4 w-4 mr-2" />
              Clôturer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
